// ============================================================================
// Reservation Data Context
// Shared state management for reservation calendar with viewport-based loading
// Reuses SiteDataContext for configuration to eliminate duplicate API calls
// ============================================================================

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

import {
  ReservationDataState,
  ReservationDataAction,
  ReservationDataContextValue,
  GridSite,
  ChunkKey,
  createChunkKey,
} from '../types/reservationData';

import { Reservation, MapSection } from '../types';
import { Site as ApiSite, StoreSiteType, StoreSiteSection } from '../types/site';

import { useSiteData } from './SiteDataContext';
import { authService } from '../services/auth';
import { getReservations, mapApiToGridReservations, isExpiredPaymentLinkHold } from '../services/reservationService';
import {
  addDays,
  getDateRange,
  getSeasonStart,
  getSeasonEnd,
  getSeasonDays,
} from '../utils/dateUtils';

// ============================================================================
// Constants
// ============================================================================

const VISIBLE_DAYS = 14; // Default visible days
const CHUNK_DAYS = 30; // Days per chunk (1 month)
const BUFFER_DAYS = 30; // Extra days before season to catch overlapping stays
const DEBOUNCE_MS = 150; // Scroll debounce

// ============================================================================
// Initial State Factory
// ============================================================================

function createInitialState(year: number): ReservationDataState {
  const seasonStartDate = getSeasonStart(year);
  const seasonEndDate = getSeasonEnd(year);
  const allDates = getDateRange(seasonStartDate, getSeasonDays(year));

  return {
    // Sites
    sites: [],
    sections: [],
    sitesLoading: false,
    sitesLoaded: false,

    // Reservations
    reservations: [],
    reservationsLoading: false,
    reservationsError: null,

    // Chunks
    loadedChunks: new Set(),
    loadingChunks: new Set(),

    // Viewport
    viewport: {
      startSiteIndex: 0,
      endSiteIndex: 30,
      startDateIndex: 0,
      endDateIndex: VISIBLE_DAYS,
      lastScrollLeft: 0,
      lastScrollTop: 0,
    },

    // Season
    selectedYear: year,
    seasonStartDate,
    seasonEndDate,
    allDates,

    // Search
    searchCode: '',
    searchEmail: '',
    searchName: '',
  };
}

// ============================================================================
// Reducer
// ============================================================================

function reservationDataReducer(
  state: ReservationDataState,
  action: ReservationDataAction
): ReservationDataState {
  switch (action.type) {
    // Sites actions
    case 'SITES_LOADING':
      return {
        ...state,
        sitesLoading: true,
      };

    case 'SITES_LOADED':
      return {
        ...state,
        sites: action.payload.sites,
        sections: action.payload.sections,
        sitesLoading: false,
        sitesLoaded: true,
      };

    // Chunk actions
    case 'CHUNK_LOADING': {
      const newLoading = new Set(state.loadingChunks);
      newLoading.add(action.payload);
      return {
        ...state,
        loadingChunks: newLoading,
        reservationsLoading: true,
      };
    }

    case 'CHUNK_LOADED': {
      const newLoading = new Set(state.loadingChunks);
      newLoading.delete(action.payload.chunkKey);
      const newLoaded = new Set(state.loadedChunks);
      newLoaded.add(action.payload.chunkKey);

      // Merge new reservations with existing, avoiding duplicates
      const existingIds = new Set(state.reservations.map((r) => `${r.id}-${r.siteId}`));
      const newReservations = action.payload.reservations.filter(
        (r) => !existingIds.has(`${r.id}-${r.siteId}`)
      );

      return {
        ...state,
        loadingChunks: newLoading,
        loadedChunks: newLoaded,
        reservations: [...state.reservations, ...newReservations],
        reservationsLoading: newLoading.size > 0,
        reservationsError: null,
      };
    }

    case 'CHUNK_ERROR': {
      const newLoading = new Set(state.loadingChunks);
      newLoading.delete(action.payload.chunkKey);
      return {
        ...state,
        loadingChunks: newLoading,
        reservationsLoading: newLoading.size > 0,
        reservationsError: action.payload.error,
      };
    }

    // Bulk reservations actions
    case 'RESERVATIONS_LOADING':
      return {
        ...state,
        reservationsLoading: true,
        reservationsError: null,
      };

    case 'RESERVATIONS_LOADED':
      return {
        ...state,
        reservations: action.payload,
        reservationsLoading: false,
        reservationsError: null,
      };

    case 'RESERVATIONS_ERROR':
      return {
        ...state,
        reservationsLoading: false,
        reservationsError: action.payload,
      };

    // Viewport actions
    case 'UPDATE_VIEWPORT':
      return {
        ...state,
        viewport: {
          ...state.viewport,
          ...action.payload,
        },
      };

    // Year actions
    case 'SET_YEAR': {
      const newSeasonStart = getSeasonStart(action.payload);
      const newSeasonEnd = getSeasonEnd(action.payload);
      const newDates = getDateRange(newSeasonStart, getSeasonDays(action.payload));
      return {
        ...state,
        selectedYear: action.payload,
        seasonStartDate: newSeasonStart,
        seasonEndDate: newSeasonEnd,
        allDates: newDates,
        reservations: [], // Clear reservations when year changes
        loadedChunks: new Set(),
        loadingChunks: new Set(),
        viewport: {
          ...state.viewport,
          startDateIndex: 0,
          endDateIndex: VISIBLE_DAYS,
        },
      };
    }

    // Search actions
    case 'SET_SEARCH':
      return {
        ...state,
        searchCode: action.payload.searchCode ?? state.searchCode,
        searchEmail: action.payload.searchEmail ?? state.searchEmail,
        searchName: action.payload.searchName ?? state.searchName,
      };

    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchCode: '',
        searchEmail: '',
        searchName: '',
      };

    // Invalidation actions
    case 'INVALIDATE_RESERVATIONS':
      return {
        ...state,
        reservations: [],
        loadedChunks: new Set(),
        loadingChunks: new Set(),
      };

    case 'INVALIDATE_CHUNKS':
      return {
        ...state,
        loadedChunks: new Set(),
        loadingChunks: new Set(),
      };

    // CRUD notifications
    case 'RESERVATION_CREATED':
      return {
        ...state,
        reservations: [...state.reservations, action.payload],
      };

    case 'RESERVATION_UPDATED':
    case 'RESERVATION_CANCELLED':
      // Invalidate to force refresh
      return {
        ...state,
        reservations: [],
        loadedChunks: new Set(),
        loadingChunks: new Set(),
      };

    default:
      return state;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map API site to grid site format
 */
function mapSiteForGrid(site: ApiSite, siteTypes: StoreSiteType[]): GridSite {
  const typeName = siteTypes.find((t) => t.id === site.type_id)?.name || 'Unknown';
  return {
    id: site.id,
    alias: site.alias,
    name: site.alias,
    siteType: typeName,
    section: String(site.site_section_id || ''),
    maxLength: site.max_length,
    seasonal: site.seasonal,
  };
}

/**
 * Map section for grid
 */
function mapSectionForGrid(section: StoreSiteSection): MapSection {
  return {
    id: String(section.id),
    name: section.name,
    siteRange: '',
  };
}

/**
 * Calculate which chunks need to be loaded for the current viewport
 */
function getRequiredChunks(
  startDateIndex: number,
  endDateIndex: number,
  allDates: string[],
  loadedChunks: Set<ChunkKey>,
  loadingChunks: Set<ChunkKey>
): ChunkKey[] {
  const required: ChunkKey[] = [];

  // Calculate start chunk (align to CHUNK_DAYS boundaries)
  const chunkStartIndex = Math.floor(startDateIndex / CHUNK_DAYS) * CHUNK_DAYS;

  // Load chunks that cover the viewport plus one prefetch buffer
  for (let i = chunkStartIndex; i <= endDateIndex + CHUNK_DAYS; i += CHUNK_DAYS) {
    if (i >= allDates.length) break;

    const chunkStart = allDates[i];
    const chunkEndIndex = Math.min(i + CHUNK_DAYS - 1, allDates.length - 1);
    const chunkEnd = allDates[chunkEndIndex];

    const key = createChunkKey(chunkStart, chunkEnd);

    if (!loadedChunks.has(key) && !loadingChunks.has(key)) {
      required.push(key);
    }
  }

  return required;
}

// ============================================================================
// Context
// ============================================================================

const ReservationDataContext = createContext<ReservationDataContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface ReservationDataProviderProps {
  children: ReactNode;
}

export function ReservationDataProvider({ children }: ReservationDataProviderProps) {
  const currentYear = new Date().getFullYear();
  const [state, dispatch] = useReducer(reservationDataReducer, createInitialState(currentYear));

  // Get site config from SiteDataContext (no duplicate API calls)
  const siteData = useSiteData();

  // Refs for debouncing
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchInProgressRef = useRef<Set<ChunkKey>>(new Set());

  // ============================================================================
  // Load Sites from SiteDataContext
  // ============================================================================

  const loadSites = useCallback(async () => {
    if (state.sitesLoaded || state.sitesLoading) {
      return;
    }

    // Wait for SiteDataContext to load store config
    if (!siteData.state.storeConfigLoaded || !siteData.state.sitesLoaded) {
      return;
    }

    dispatch({ type: 'SITES_LOADING' });

    const storeConfig = siteData.state.storeConfig;
    if (!storeConfig) return;

    // Transform sites from SiteDataContext
    const gridSites = siteData.state.sites.map((site) =>
      mapSiteForGrid(
        {
          id: site.id,
          store_id: site.store_id,
          alias: site.alias,
          description: site.description,
          type_id: site.type_id,
          site_section_id: site.site_section_id,
          max_length: site.max_length,
          seasonal: site.seasonal,
        } as ApiSite,
        storeConfig.siteTypes
      )
    );

    // Get sections
    const activeSections = storeConfig.sections.filter(
      (s: StoreSiteSection) => s.active !== false
    );
    const gridSections = activeSections.map(mapSectionForGrid);

    dispatch({
      type: 'SITES_LOADED',
      payload: { sites: gridSites, sections: gridSections },
    });
  }, [
    state.sitesLoaded,
    state.sitesLoading,
    siteData.state.storeConfigLoaded,
    siteData.state.sitesLoaded,
    siteData.state.storeConfig,
    siteData.state.sites,
  ]);

  // Auto-load sites when SiteDataContext is ready
  useEffect(() => {
    if (siteData.state.storeConfigLoaded && siteData.state.sitesLoaded && !state.sitesLoaded) {
      loadSites();
    }
  }, [siteData.state.storeConfigLoaded, siteData.state.sitesLoaded, state.sitesLoaded, loadSites]);

  // ============================================================================
  // Chunk Loading
  // ============================================================================

  const loadChunk = useCallback(
    async (chunkKey: ChunkKey) => {
      // Prevent duplicate fetches
      if (
        state.loadedChunks.has(chunkKey) ||
        state.loadingChunks.has(chunkKey) ||
        fetchInProgressRef.current.has(chunkKey)
      ) {
        return;
      }

      fetchInProgressRef.current.add(chunkKey);
      dispatch({ type: 'CHUNK_LOADING', payload: chunkKey });

      try {
        const storeId = authService.getStoreId();
        const [, startDate, endDate] = chunkKey.split(':');

        // Add buffer before start date to catch reservations that started earlier
        const fetchStart = addDays(startDate, -BUFFER_DAYS);
        const fetchEnd = endDate;

        const apiData = await getReservations({
          storeId,
          checkInFrom: fetchStart,
          checkInTo: fetchEnd,
          activeOnly: true,
        });

        const reservations = mapApiToGridReservations(apiData);

        dispatch({
          type: 'CHUNK_LOADED',
          payload: { chunkKey, reservations },
        });
      } catch (error) {
        console.error(`Failed to load chunk ${chunkKey}:`, error);
        dispatch({
          type: 'CHUNK_ERROR',
          payload: {
            chunkKey,
            error: error instanceof Error ? error.message : 'Failed to load reservations',
          },
        });
      } finally {
        fetchInProgressRef.current.delete(chunkKey);
      }
    },
    [state.loadedChunks, state.loadingChunks]
  );

  const loadViewportChunks = useCallback(async () => {
    const { startDateIndex, endDateIndex } = state.viewport;
    const requiredChunks = getRequiredChunks(
      startDateIndex,
      endDateIndex,
      state.allDates,
      state.loadedChunks,
      state.loadingChunks
    );

    // Load all required chunks in parallel
    await Promise.all(requiredChunks.map(loadChunk));
  }, [state.viewport, state.allDates, state.loadedChunks, state.loadingChunks, loadChunk]);

  // Initial chunk load
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (state.sitesLoaded && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadViewportChunks();
    }
  }, [state.sitesLoaded, loadViewportChunks]);

  // ============================================================================
  // Scroll Handling
  // ============================================================================

  const onScroll = useCallback(
    (scrollLeft: number, scrollTop: number) => {
      // Debounce scroll events
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }

      scrollDebounceRef.current = setTimeout(() => {
        // Calculate new viewport indices based on scroll position
        // These values would normally come from the grid component
        // For now, we estimate based on scroll position
        const CELL_WIDTH = 45; // Approximate cell width
        const ROW_HEIGHT = 40; // Approximate row height
        const SITE_COL_WIDTH = 220; // Site column width

        const adjustedScrollLeft = Math.max(0, scrollLeft - SITE_COL_WIDTH);
        const startDateIndex = Math.floor(adjustedScrollLeft / CELL_WIDTH);
        const endDateIndex = Math.min(
          startDateIndex + VISIBLE_DAYS + 5,
          state.allDates.length - 1
        );

        const startSiteIndex = Math.floor(scrollTop / ROW_HEIGHT);
        const endSiteIndex = Math.min(startSiteIndex + 30, state.sites.length - 1);

        dispatch({
          type: 'UPDATE_VIEWPORT',
          payload: {
            startDateIndex,
            endDateIndex,
            startSiteIndex,
            endSiteIndex,
            lastScrollLeft: scrollLeft,
            lastScrollTop: scrollTop,
          },
        });

        // Load any new chunks needed
        loadViewportChunks();
      }, DEBOUNCE_MS);
    },
    [state.allDates.length, state.sites.length, loadViewportChunks]
  );

  // ============================================================================
  // Year Control
  // ============================================================================

  const setYear = useCallback((year: number) => {
    initialLoadDone.current = false;
    dispatch({ type: 'SET_YEAR', payload: year });
  }, []);

  // Load chunks when year changes
  useEffect(() => {
    if (state.sitesLoaded && state.reservations.length === 0 && state.loadedChunks.size === 0) {
      loadViewportChunks();
    }
  }, [state.selectedYear, state.sitesLoaded, state.reservations.length, state.loadedChunks.size, loadViewportChunks]);

  // ============================================================================
  // Search Actions
  // ============================================================================

  const setSearch = useCallback(
    (search: { searchCode?: string; searchEmail?: string; searchName?: string }) => {
      dispatch({ type: 'SET_SEARCH', payload: search });
    },
    []
  );

  const clearSearch = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH' });
  }, []);

  // ============================================================================
  // Refresh
  // ============================================================================

  const refreshReservations = useCallback(async () => {
    dispatch({ type: 'INVALIDATE_RESERVATIONS' });
    // Will reload via useEffect
    await loadViewportChunks();
  }, [loadViewportChunks]);

  // ============================================================================
  // Cache Management
  // ============================================================================

  const invalidateReservations = useCallback(() => {
    dispatch({ type: 'INVALIDATE_RESERVATIONS' });
  }, []);

  // ============================================================================
  // CRUD Notifications
  // ============================================================================

  const onReservationCreated = useCallback((reservation: Reservation) => {
    dispatch({ type: 'RESERVATION_CREATED', payload: reservation });
  }, []);

  const onReservationUpdated = useCallback((reservationId: number) => {
    dispatch({ type: 'RESERVATION_UPDATED', payload: reservationId });
  }, []);

  const onReservationCancelled = useCallback((reservationId: number) => {
    dispatch({ type: 'RESERVATION_CANCELLED', payload: reservationId });
  }, []);

  // ============================================================================
  // Utility Functions
  // ============================================================================

  const getChunkKey = useCallback((startDate: string, endDate: string): ChunkKey => {
    return createChunkKey(startDate, endDate);
  }, []);

  const isChunkLoaded = useCallback(
    (chunkKey: ChunkKey): boolean => {
      return state.loadedChunks.has(chunkKey);
    },
    [state.loadedChunks]
  );

  const isChunkLoading = useCallback(
    (chunkKey: ChunkKey): boolean => {
      return state.loadingChunks.has(chunkKey);
    },
    [state.loadingChunks]
  );

  // ============================================================================
  // Computed Values
  // ============================================================================

  const { visibleReservations, hasSearch, firstMatchId } = useMemo(() => {
    // Display all reservations without viewport-based filtering
    // Filter only by active statuses. Payment-link holds drop off once their
    // payment window lapses — the backend frees the site for new bookings at
    // that point (availability checks expires_at), so keeping the block on
    // the schedule would show a site as taken that others can already book.
    // The reservation itself stays in the member profile (fetched with
    // active_only=false).
    let filtered = state.reservations.filter(
      (res) =>
        res.status === 'confirmed' || res.status === 'checked_in' || res.status === 'checked_out' ||
        (res.status === 'pending_payment' && res.hasPaymentLink === true && !isExpiredPaymentLinkHold(res))
    );

    // Apply search filters
    const hasSearchQuery = !!(
      state.searchCode.trim() ||
      state.searchEmail.trim() ||
      state.searchName.trim()
    );

    if (state.searchCode.trim()) {
      const q = state.searchCode.trim().toLowerCase();
      filtered = filtered.filter((res) => res.confirmationCode.toLowerCase().includes(q));
    }
    if (state.searchEmail.trim()) {
      const q = state.searchEmail.trim().toLowerCase();
      filtered = filtered.filter((res) => res.guestEmail.toLowerCase().includes(q));
    }
    if (state.searchName.trim()) {
      const q = state.searchName.trim().toLowerCase();
      filtered = filtered.filter((res) => res.guestName.toLowerCase().includes(q));
    }

    const firstMatch = hasSearchQuery && filtered.length > 0 ? filtered[0].id : undefined;

    return {
      visibleReservations: filtered,
      hasSearch: hasSearchQuery,
      firstMatchId: firstMatch,
    };
  }, [
    state.reservations,
    state.searchCode,
    state.searchEmail,
    state.searchName,
  ]);

  const visibleSites = useMemo(() => {
    const { startSiteIndex, endSiteIndex } = state.viewport;
    return state.sites.slice(startSiteIndex, endSiteIndex + 1);
  }, [state.sites, state.viewport]);

  const visibleDates = useMemo(() => {
    const { startDateIndex, endDateIndex } = state.viewport;
    return state.allDates.slice(startDateIndex, endDateIndex + 1);
  }, [state.allDates, state.viewport]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<ReservationDataContextValue>(
    () => ({
      state,
      visibleReservations,
      visibleSites,
      visibleDates,
      hasSearch,
      firstMatchId,
      loadViewportChunks,
      onScroll,
      loadSites,
      refreshReservations,
      setYear,
      setSearch,
      clearSearch,
      invalidateReservations,
      onReservationCreated,
      onReservationUpdated,
      onReservationCancelled,
      getChunkKey,
      isChunkLoaded,
      isChunkLoading,
    }),
    [
      state,
      visibleReservations,
      visibleSites,
      visibleDates,
      hasSearch,
      firstMatchId,
      loadViewportChunks,
      onScroll,
      loadSites,
      refreshReservations,
      setYear,
      setSearch,
      clearSearch,
      invalidateReservations,
      onReservationCreated,
      onReservationUpdated,
      onReservationCancelled,
      getChunkKey,
      isChunkLoaded,
      isChunkLoading,
    ]
  );

  return (
    <ReservationDataContext.Provider value={value}>{children}</ReservationDataContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useReservationData(): ReservationDataContextValue {
  const context = useContext(ReservationDataContext);
  if (!context) {
    throw new Error('useReservationData must be used within a ReservationDataProvider');
  }
  return context;
}

export default ReservationDataContext;
